/**
 * RawInputTab Component
 *
 * Displays the original CSV data in a read-only table format:
 * - Column headers from CSV
 * - Row data
 * - Pagination for large datasets
 * - Groundtruth column selection for evaluation
 * - Evaluator function configuration
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { batchService, jobService } from '../../services/api';
import { evaluationService } from '../../services/evaluationApi';
import { useBatch } from '../../hooks/useBatches';
import { authenticatedFetch } from '../../utils/authenticatedFetch';
import toast from 'react-hot-toast';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

interface RawInputTabProps {
  batchId: string;
}

export const RawInputTab: React.FC<RawInputTabProps> = ({ batchId }) => {
  const [page, setPage] = useState<number>(0);
  const rowsPerPage = 50;
  const queryClient = useQueryClient();

  // Fetch batch to get current groundtruth columns and evaluator settings
  const { data: batch } = useBatch(batchId);

  // Groundtruth selection state
  const [selectedColumns, setSelectedColumns] = useState<string[]>(batch?.groundtruth_columns || []);
  const [evaluatorDescription, setEvaluatorDescription] = useState(batch?.evaluator_description || '');
  const [generatedEvaluator, setGeneratedEvaluator] = useState(batch?.evaluator_function || '');
  const [isGenerating, setIsGenerating] = useState(false);
  const [showEvaluatorConfig, setShowEvaluatorConfig] = useState(false);

  // Groundtruth editing state
  const [isEditingGroundtruth, setIsEditingGroundtruth] = useState(false);
  const [editedGroundtruth, setEditedGroundtruth] = useState<Record<string, Record<string, any>>>({});

  // Update selected columns when batch loads
  React.useEffect(() => {
    if (batch?.groundtruth_columns) {
      setSelectedColumns(batch.groundtruth_columns);
    }
    if (batch?.evaluator_description) {
      setEvaluatorDescription(batch.evaluator_description);
    }
    if (batch?.evaluator_function) {
      setGeneratedEvaluator(batch.evaluator_function);
    }
  }, [batch]);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['batch-raw-input', batchId],
    queryFn: () => batchService.getRawInput(batchId),
    staleTime: 300000, // 5 minutes - raw input doesn't change
  });

  // Toggle column selection
  const toggleColumn = (column: string) => {
    setSelectedColumns(prev =>
      prev.includes(column)
        ? prev.filter(c => c !== column)
        : [...prev, column]
    );
  };

  // Generate evaluator function
  const handleGenerateEvaluator = async () => {
    if (!evaluatorDescription.trim()) {
      toast.error('Please provide an evaluation description');
      return;
    }
    if (selectedColumns.length === 0) {
      toast.error('Please select at least one groundtruth column');
      return;
    }

    setIsGenerating(true);
    try {
      const sampleRow = data?.rows[0] || {};
      const response = await evaluationService.generateEvaluator(batchId, {
        description: evaluatorDescription,
        groundtruth_columns: selectedColumns,
        sample_csv_row: sampleRow,
      });
      setGeneratedEvaluator(response.function_code);
      toast.success('Evaluator function generated successfully');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to generate evaluator');
    } finally {
      setIsGenerating(false);
    }
  };

  // Save evaluation configuration
  const saveEvaluationMutation = useMutation({
    mutationFn: async () => {
      console.log('Saving evaluation config:', {
        groundtruth_columns: selectedColumns,
        evaluator_description: evaluatorDescription,
        has_evaluator_function: !!generatedEvaluator
      });
      const result = await batchService.update(batchId, {
        groundtruth_columns: selectedColumns.length > 0 ? selectedColumns : undefined,
        evaluator_description: evaluatorDescription.trim() || undefined,
        evaluator_function: generatedEvaluator || undefined,
      });
      console.log('Save successful, result:', result);
      return result;
    },
    onSuccess: () => {
      console.log('Mutation onSuccess called');
      toast.success('Evaluation configuration saved');
      queryClient.invalidateQueries({ queryKey: ['batches', batchId] });
      queryClient.invalidateQueries({ queryKey: ['batch-jobs', batchId] });
      setShowEvaluatorConfig(false);
    },
    onError: (error: Error) => {
      console.error('Mutation onError called:', error);
      toast.error(error.message || 'Failed to save evaluation configuration');
    },
  });

  // Save edited groundtruth values
  const saveGroundtruthMutation = useMutation({
    mutationFn: async () => {
      // Fetch all jobs in batches (max 1000 per request)
      let allJobs: any[] = [];
      let offset = 0;
      const limit = 1000;

      while (true) {
        const jobsResponse = await authenticatedFetch(`${API_BASE_URL}/api/batches/${batchId}/jobs?limit=${limit}&offset=${offset}`);
        if (!jobsResponse.ok) throw new Error('Failed to fetch jobs');
        const jobsData = await jobsResponse.json();

        allJobs = allJobs.concat(jobsData.jobs);

        if (jobsData.jobs.length < limit) break; // No more jobs
        offset += limit;
      }

      // Update each job with edited groundtruth values
      const updates = Object.entries(editedGroundtruth).map(async ([rowKey, gtValues]) => {
        const rowIndex = gtValues.row_index;
        const job = allJobs.find((j: any) => j.row_id === rowIndex);
        if (!job) return null;

        // Remove row_index from gtValues before saving
        const { row_index, ...cleanGtValues } = gtValues;

        // Update raw_input with the new groundtruth values
        const updatedRawInput = { ...(job.raw_input || {}) };
        Object.entries(cleanGtValues).forEach(([key, value]) => {
          updatedRawInput[key] = value;
        });

        // Update both groundtruth and raw_input
        const response = await authenticatedFetch(`${API_BASE_URL}/api/jobs/${job.job_id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            groundtruth: cleanGtValues,
            raw_input: updatedRawInput
          }),
        });
        if (!response.ok) throw new Error(`Failed to update job ${job.job_id}`);
        return response.json();
      });
      await Promise.all(updates.filter(u => u !== null));
      return Object.keys(editedGroundtruth).length;
    },
    onSuccess: (count) => {
      toast.success(`Updated ${count} groundtruth values`);
      setIsEditingGroundtruth(false);
      setEditedGroundtruth({});
      queryClient.invalidateQueries({ queryKey: ['batch-jobs', batchId] });
      refetch();
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to save groundtruth values');
    },
  });

  // Export full CSV with updated groundtruth values
  const handleExportGroundtruth = () => {
    if (!data) {
      toast.error('No data to export');
      return;
    }

    // Use all CSV headers, not just groundtruth columns
    const headers = data.csv_headers;
    const csvRows = [headers.join(',')];

    data.rows.forEach((row, index) => {
      const rowData = headers.map(header => {
        // Check if this is a groundtruth column with edited value
        let value = row[header] || '';

        // If groundtruth column and we have edited values, use them
        const rowKey = `row_${index}`;
        if (batch?.groundtruth_columns?.includes(header) && editedGroundtruth[rowKey]?.[header] !== undefined) {
          value = editedGroundtruth[rowKey][header];
        }

        // Escape commas and quotes in CSV
        const strValue = String(value);
        if (strValue.includes(',') || strValue.includes('"') || strValue.includes('\n')) {
          return `"${strValue.replace(/"/g, '""')}"`;
        }
        return strValue;
      });
      csvRows.push(rowData.join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${batch?.name || 'batch'}-updated.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast.success('CSV exported with updated groundtruth values');
  };

  // Handle groundtruth cell edit (using row_id as key since raw input doesn't have job_id)
  const handleGroundtruthEdit = (rowIndex: number, column: string, value: string) => {
    const row = data?.rows[rowIndex];
    if (!row) return;

    // Use row index as key since raw input doesn't have job_id
    const rowKey = `row_${rowIndex}`;
    setEditedGroundtruth(prev => ({
      ...prev,
      [rowKey]: {
        ...(prev[rowKey] || {}),
        [column]: value,
        row_index: rowIndex  // Store row index for later job matching
      }
    }));
  };

  const getGroundtruthValue = (rowIndex: number, column: string) => {
    const row = data?.rows[rowIndex];
    if (!row) return '';

    const rowKey = `row_${rowIndex}`;
    if (editedGroundtruth[rowKey]?.[column] !== undefined) {
      return editedGroundtruth[rowKey][column];
    }

    return row[column] || '';
  };

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error loading raw input</h3>
            <p className="mt-1 text-sm text-red-700">{error.message}</p>
            <div className="mt-4">
              <button
                onClick={() => refetch()}
                className="text-sm font-medium text-red-600 hover:text-red-500"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" aria-label="Loading raw input"></div>
      </div>
    );
  }

  if (!data || data.rows.length === 0) {
    return (
      <div className="text-center py-12 bg-white border border-gray-200 rounded-lg">
        <svg
          className="mx-auto h-12 w-12 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900">No data available</h3>
        <p className="mt-1 text-sm text-gray-500">
          CSV data could not be loaded
        </p>
      </div>
    );
  }

  const totalPages = Math.ceil(data.rows.length / rowsPerPage);
  const startIdx = page * rowsPerPage;
  const endIdx = startIdx + rowsPerPage;
  const currentRows = data.rows.slice(startIdx, endIdx);

  return (
    <div className="space-y-4">
      {/* Evaluation Configuration Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Evaluation Configuration</h3>
          <button
            onClick={() => setShowEvaluatorConfig(!showEvaluatorConfig)}
            className="px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-800"
          >
            {showEvaluatorConfig ? 'Hide' : 'Configure Evaluation'}
          </button>
        </div>

        {batch?.groundtruth_columns && batch.groundtruth_columns.length > 0 && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded">
            <p className="text-sm text-green-800">
              <strong>Groundtruth columns:</strong> {batch.groundtruth_columns.join(', ')}
            </p>
            {batch.evaluator_description && (
              <p className="text-sm text-green-700 mt-1">
                <strong>Evaluation criteria:</strong> {batch.evaluator_description}
              </p>
            )}
          </div>
        )}

        {showEvaluatorConfig && (
          <div className="space-y-4 border-t pt-4">
            {/* Column Selection */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Select Groundtruth Columns</h4>
              <p className="text-xs text-gray-500 mb-3">
                Choose columns from your CSV that contain expected values for comparison
              </p>
              <div className="flex flex-wrap gap-2">
                {data?.csv_headers.map((header) => (
                  <button
                    key={header}
                    onClick={() => toggleColumn(header)}
                    className={`px-3 py-1.5 text-sm font-medium rounded-md border transition-colors ${
                      selectedColumns.includes(header)
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {header}
                  </button>
                ))}
              </div>
            </div>

            {/* Evaluator Description */}
            {selectedColumns.length > 0 && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Evaluation Criteria Description (Optional)
                  </label>
                  <textarea
                    value={evaluatorDescription}
                    onChange={(e) => setEvaluatorDescription(e.target.value)}
                    rows={4}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    placeholder="Describe how to evaluate job results against groundtruth columns. You can generate a function from this or write your own below..."
                  />
                </div>

                {/* Generate Button */}
                {evaluatorDescription.trim() && (
                  <div className="flex gap-3">
                    <button
                      onClick={handleGenerateEvaluator}
                      disabled={isGenerating}
                      className="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isGenerating ? 'Generating...' : 'Generate Evaluator Function'}
                    </button>
                  </div>
                )}

                {/* Evaluator Function Editor - Always visible */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Evaluator Function
                  </label>
                  <textarea
                    value={generatedEvaluator}
                    onChange={(e) => setGeneratedEvaluator(e.target.value)}
                    rows={20}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 font-mono text-xs"
                    placeholder="def evaluate_output(output, groundtruth):\n    # Your evaluation logic here\n    # output: dict with job execution result\n    # groundtruth: dict with expected values\n    return True  # Return True if passed, False if failed"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Write or edit the evaluator function. It must define <code>evaluate_output(output, groundtruth)</code> and return a boolean.
                  </p>
                </div>

                {/* Save Button - Always visible when function has content */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button
                    onClick={() => setShowEvaluatorConfig(false)}
                    className="px-4 py-2 bg-white text-gray-700 text-sm font-medium rounded-md border border-gray-300 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => saveEvaluationMutation.mutate()}
                    disabled={saveEvaluationMutation.isPending || !generatedEvaluator.trim()}
                    className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {saveEvaluationMutation.isPending ? 'Saving...' : 'Save Evaluation Configuration'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Info Card with Actions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex justify-between items-start">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">Original CSV Data</h3>
              <p className="mt-1 text-sm text-blue-700">
                This is the raw CSV data uploaded for this batch. Total rows: {data.total_rows}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            {batch?.groundtruth_columns && batch.groundtruth_columns.length > 0 && (
              <>
                <button
                  onClick={handleExportGroundtruth}
                  className="px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 border border-blue-300 rounded hover:bg-blue-50"
                >
                  Export CSV
                </button>
                {!isEditingGroundtruth ? (
                  <button
                    onClick={() => setIsEditingGroundtruth(true)}
                    className="px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 border border-blue-300 rounded hover:bg-blue-50"
                  >
                    Edit Groundtruth
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setIsEditingGroundtruth(false);
                        setEditedGroundtruth({});
                      }}
                      className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-800 border border-gray-300 rounded hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => saveGroundtruthMutation.mutate()}
                      disabled={Object.keys(editedGroundtruth).length === 0 || saveGroundtruthMutation.isPending}
                      className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
                    >
                      {saveGroundtruthMutation.isPending ? 'Saving...' : `Save Changes (${Object.keys(editedGroundtruth).length})`}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* CSV Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Row #
                </th>
                {data.csv_headers.map((header, idx) => {
                  const isGroundtruth = batch?.groundtruth_columns?.includes(header);
                  return (
                    <th
                      key={idx}
                      scope="col"
                      className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                        isGroundtruth
                          ? 'bg-green-100 text-green-800'
                          : 'text-gray-500'
                      }`}
                    >
                      <div className="flex items-center gap-1">
                        {header}
                        {isGroundtruth && (
                          <svg className="h-4 w-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentRows.map((row, rowIdx) => (
                <tr key={startIdx + rowIdx} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {startIdx + rowIdx + 1}
                  </td>
                  {data.csv_headers.map((header, colIdx) => {
                    const isGroundtruthCol = batch?.groundtruth_columns?.includes(header);
                    const isEditable = isEditingGroundtruth && isGroundtruthCol;

                    return (
                      <td key={colIdx} className={`px-6 py-4 text-sm ${isGroundtruthCol ? 'bg-green-50' : 'text-gray-500'}`}>
                        {isEditable ? (
                          <input
                            type="text"
                            value={getGroundtruthValue(rowIdx, header)}
                            onChange={(e) => handleGroundtruthEdit(startIdx + rowIdx, header, e.target.value)}
                            className="w-full px-2 py-1 text-sm border border-green-300 rounded focus:ring-2 focus:ring-green-500 focus:border-green-500"
                          />
                        ) : (
                          <div className="max-w-xs truncate" title={String(row[header] || '')}>
                            {String(row[header] || '')}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setPage(Math.max(0, page - 1))}
                disabled={page === 0}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Previous page"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                disabled={page >= totalPages - 1}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Next page"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{startIdx + 1}</span> to{' '}
                  <span className="font-medium">{Math.min(endIdx, data.rows.length)}</span> of{' '}
                  <span className="font-medium">{data.rows.length}</span> rows
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => setPage(Math.max(0, page - 1))}
                    disabled={page === 0}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label="Previous page"
                  >
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path
                        fillRule="evenodd"
                        d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                  <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                    Page {page + 1} of {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                    disabled={page >= totalPages - 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label="Next page"
                  >
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path
                        fillRule="evenodd"
                        d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
